import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import get from 'lodash/get';

import type { AppThunk } from 'src/actions/shared/sharedTypes';

import ApiToken from 'src/models/Integrations/apiToken';
import createCollectionResource from 'src/actions/shared/createResource';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { logout } from 'src/actions/auth/authSlice';
import { toastSuccess, toastError } from 'src/actions/toast';

const apiTokensResource = createCollectionResource<ApiToken>({
  name: 'apiTokens',
  observerCreator: ({ actions }) => {
    return ({ dispatch, getState }) => {
      return {
        onBroadcast(item) {
          const token = find(get(getState(), 'apiTokens.values', []), token => token.id === item.id);
          if (token) {
            // if api token has token we should show it until refresh
            const changedToken = { ...item, token: get(token, 'token') };
            dispatch(actions.broadcast(cloneDeep(changedToken)));
          } else {
            dispatch(actions.broadcast(item));
          }
        },
        onBroadcastDelete(_: any, params) {
          dispatch(actions.broadcastDelete(params.id));
        },
      };
    };
  },

  endpoint: 'v1/orgs/:orgId/users/:agentId/tokens/:id',
  options: {
    toastMessages: {
      onUpdate: () => 'Token renamed',
      onRemove: () => 'Token deleted',
    },
  },
  reducers: {
    addSuccess(state, action) {
      state.persistingState = 'succeeded';
      const newState = cloneDeep(state.values);
      newState.push(action.payload);
      state.values = newState;
    },
  },
});

const add: AppThunk =
  (params: any, attrs: any) =>
  async (dispatch, _, { gateways }) => {
    const { addStarted, addSuccess, addError, addRequestError } = apiTokensResource.actions;

    try {
      dispatch(addStarted({}));
      const newToken = await gateways.apiTokens.add({ ...params }, attrs);
      dispatch(addSuccess(newToken));

      dispatch(toastSuccess('Token created'));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(addError(err.errors));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(addRequestError(err));
      }
      dispatch(toastError(`There was an error while adding`));
    }
  };

const copyToClipboard: AppThunk = (apiToken: ApiToken) => async dispatch => {
  try {
    if (!apiToken.token) {
      return;
    }
    await navigator.clipboard.writeText(apiToken.token);
    dispatch(toastSuccess('Token copied'));
  } catch (e) {
    dispatch(toastError('Clipboard copying failed'));
  }
};

apiTokensResource.effects.add = add;
apiTokensResource.effects.copyToClipboard = copyToClipboard;

export default apiTokensResource;
